import type { CommandGroup } from "./groups";

/**
 * Find the href of the single command item whose pathname exactly matches
 * the current page.
 *
 * When multiple items share the same pathname (e.g. "Logs" at /logs and
 * "Workspace metrics" at /logs?flag=true), the item without a query string
 * wins. Fragment links to controls are excluded from this page-level label.
 *
 * Uses URL() with the provided origin as base so both absolute and relative
 * hrefs resolve correctly.
 *
 * Query parameters can represent other views, so pathname matching is only
 * a page-level approximation.
 */
export function findCurrentPageHref(
  groups: CommandGroup[],
  currentPath: string,
  origin: string,
): string | null {
  let bestHref: string | null = null;
  let bestHasQuery = true;

  for (const { items } of groups) {
    for (const item of items) {
      // Items with replaceState are overlays/actions on the current page,
      // not navigation destinations — skip them for current-page detection.
      if (item.replaceState) continue;

      const parsed = new URL(item.href, origin);
      if (currentPath !== parsed.pathname || parsed.hash) continue;

      // On tie, prefer href without query string (see JSDoc).
      const itemHasQuery = parsed.search.length > 0;
      if (!bestHref || (!itemHasQuery && bestHasQuery)) {
        bestHref = item.href;
        bestHasQuery = itemHasQuery;
      }
    }
  }

  return bestHref;
}
